import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NeuralCanvas from '../../components/NeuralCanvas';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import './Privacy.css';

// Componente para el skeleton loader
const SkeletonLoader = () => (
  <div className="skeleton-container">
    <div className="skeleton-header"></div>
    <div className="skeleton-section">
      <div className="skeleton-title"></div>
      <div className="skeleton-text"></div>
      <div className="skeleton-text"></div>
    </div>
    <div className="skeleton-button"></div>
  </div>
);

const Privacy = () => {
  const { t, ready } = useTranslation();
  
  // Aseguramos que se pueda hacer scroll
  useEffect(() => {
    // Resetear cualquier restricción de scroll
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    
    return () => {
      // Limpiar al desmontar
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, []);

  // Si las traducciones no están listas, mostramos el skeleton
  if (!ready) {
    return (
      <div className="privacy-wrapper">
        <div className="overlay"></div>
        <NeuralCanvas />
        <div className="privacy-container">
          <SkeletonLoader />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overlay"></div>
      <NeuralCanvas />
      
      {/* Selector de idioma */}
      <div className="language-selector-container">
        <LanguageSelector />
      </div>
      
      {/* Botón para volver a la página principal */}
      <div className="button-container">
        <a href="/" className="back-button">
          {t('common.home')}
        </a>
      </div>
      
      <main className="privacy-wrapper">
        <div className="privacy-container">
          
          <div className="privacy-header">
            <h1>{t('privacy.title')}</h1>
            <p className="effective-date">{t('privacy.effectiveDate')}</p>
          </div>
          
          <div className="privacy-content">
            {/* Introducción */}
            <section className="privacy-section">
              <h2>{t('privacy.introduction.title')}</h2>
              <p>{t('privacy.introduction.content')}</p>
            </section>
            
            {/* Información que Recopilamos */}
            <section className="privacy-section">
              <h2>{t('privacy.information.title')}</h2>
              <p>{t('privacy.information.intro')}</p>
              
              <div className="subsection">
                <h3>{t('privacy.information.personal.title')}</h3>
                <ul>
                  {t('privacy.information.personal.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.information.usage.title')}</h3>
                <ul>
                  {t('privacy.information.usage.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.information.volunteers.title')}</h3>
                <ul>
                  {t('privacy.information.volunteers.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </section>
            
            {/* Cómo Usamos tu Información */}
            <section className="privacy-section">
              <h2>{t('privacy.usage.title')}</h2>
              <p>{t('privacy.usage.intro')}</p>
              
              <div className="subsection">
                <h3>{t('privacy.usage.management.title')}</h3>
                <ul>
                  {t('privacy.usage.management.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.usage.improvement.title')}</h3>
                <ul>
                  {t('privacy.usage.improvement.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.usage.communication.title')}</h3>
                <ul>
                  {t('privacy.usage.communication.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.usage.legal.title')}</h3>
                <ul>
                  {t('privacy.usage.legal.items', { returnObjects: true }).map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              </div>
            </section>
            
            {/* Compartir tu Información */}
            <section className="privacy-section">
              <h2>{t('privacy.sharing.title')}</h2>
              <p>{t('privacy.sharing.intro')}</p>
              
              <div className="subsection">
                <h3>{t('privacy.sharing.consent.title')}</h3>
                <p>{t('privacy.sharing.consent.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.sharing.providers.title')}</h3>
                <p>{t('privacy.sharing.providers.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.sharing.compliance.title')}</h3>
                <p>{t('privacy.sharing.compliance.content')}</p>
              </div>
            </section>
            
            {/* Protección de tus Datos */}
            <section className="privacy-section">
              <h2>{t('privacy.protection.title')}</h2>
              <p>{t('privacy.protection.content')}</p>
              <ul>
                {t('privacy.protection.items', { returnObjects: true }).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>
            
            {/* Tus Derechos */}
            <section className="privacy-section">
              <h2>{t('privacy.rights.title')}</h2>
              <p>{t('privacy.rights.intro')}</p>
              
              <div className="subsection">
                <h3>{t('privacy.rights.access.title')}</h3>
                <p>{t('privacy.rights.access.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.rights.rectification.title')}</h3>
                <p>{t('privacy.rights.rectification.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.rights.deletion.title')}</h3>
                <p>{t('privacy.rights.deletion.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.rights.limitation.title')}</h3>
                <p>{t('privacy.rights.limitation.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.rights.portability.title')}</h3>
                <p>{t('privacy.rights.portability.content')}</p>
              </div>
              
              <div className="subsection">
                <h3>{t('privacy.rights.opposition.title')}</h3>
                <p>{t('privacy.rights.opposition.content')}</p>
              </div>
              
              <p className="contact-info">{t('privacy.rights.contact')}</p>
            </section>
            
            {/* Retención de Datos */}
            <section className="privacy-section">
              <h2>{t('privacy.retention.title')}</h2>
              <p>{t('privacy.retention.content')}</p>
            </section>
            
            {/* Cambios en esta Política */}
            <section className="privacy-section">
              <h2>{t('privacy.changes.title')}</h2>
              <p>{t('privacy.changes.content')}</p>
            </section>
            
            {/* Contacto */}
            <section className="privacy-section">
              <h2>{t('privacy.contact.title')}</h2>
              <p>{t('privacy.contact.content')}</p>
              <p className="contact-email">{t('privacy.contact.email')}</p>
            </section>
            
            {/* Cookies y Tecnologías Similares */}
            <section className="privacy-section">
              <h2>{t('privacy.cookies.title')}</h2>
              <p>{t('privacy.cookies.content')}</p>
            </section>
            
            {/* Transferencias Internacionales de Datos */}
            <section className="privacy-section">
              <h2>{t('privacy.international.title')}</h2>
              <p>{t('privacy.international.content')}</p>
            </section>
            
            {/* Conclusión */}
            <section className="privacy-section">
              <h2>{t('privacy.conclusion.title')}</h2>
              <p>{t('privacy.conclusion.content')}</p>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default Privacy; 